import { useEffect, useState } from "react";
import { api, ApiError, type FlowTemplate, type TemplateVersion } from "../../api/client";
import { navigateTo, pathForTab } from "../../app/tabRoutes";
import { readCsrfCookie } from "../auth/csrfCookie";
import { templateSearch } from "./templateRoute";

function describeError(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "something went wrong";
}

/**
 * This tenant's SAVED templates — GET /v1/templates, not GET /v1/flows.
 *
 * The difference is the point of Slice DK. /v1/flows lists every published
 * version, which includes the throwaway flow the place-a-call form
 * publishes on every quick message; this lists what someone actually
 * saved, once per template, with its current version number.
 *
 * Delete archives. calls.flow_version_id and adhoc_calls.flow_version_id
 * are foreign keys into a template's versions, so removing one would take
 * its own call history with it.
 */
export function FlowsListPanel({ canPlaceTestCall }: { canPlaceTestCall: boolean }) {
  const [templates, setTemplates] = useState<FlowTemplate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [versions, setVersions] = useState<Record<string, TemplateVersion[]>>({});

  const load = () => {
    api
      .listTemplates()
      .then(({ templates }) => setTemplates(templates))
      .catch((err: unknown) => setError(describeError(err)));
  };

  useEffect(() => {
    let cancelled = false;
    api
      .listTemplates()
      .then(({ templates }) => {
        if (!cancelled) setTemplates(templates);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(describeError(err));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const copy = async (versionId: string) => {
    try {
      await navigator.clipboard.writeText(versionId);
      setCopiedId(versionId);
      window.setTimeout(() => setCopiedId((current) => (current === versionId ? null : current)), 1500);
    } catch {
      // Clipboard access can be blocked (permissions, insecure context);
      // the id is still shown and selectable as plain text either way.
    }
  };

  const openInDesigner = (flowId: string, options: { versionId?: string; readOnly: boolean }) => {
    navigateTo(pathForTab("flows") + templateSearch({ flowId, ...options }));
  };

  // Earlier versions are only fetched when someone asks to see them: most
  // templates have one, and the list would otherwise make a request per
  // row for history nobody opened.
  const toggleVersions = async (flowId: string) => {
    if (expanded === flowId) {
      setExpanded(null);
      return;
    }
    setExpanded(flowId);
    if (versions[flowId]) return;
    try {
      const { versions: list } = await api.listTemplateVersions(flowId);
      setVersions((current) => ({ ...current, [flowId]: list }));
    } catch (err) {
      setError(describeError(err));
    }
  };

  const archive = async (flowId: string) => {
    setBusy(flowId);
    setError(null);
    try {
      await api.archiveTemplate(readCsrfCookie(document.cookie), flowId);
      setConfirming(null);
      load();
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <section aria-labelledby="flows-list-heading" className="card">
      <h2 id="flows-list-heading">Templates</h2>
      <p className="card__note">
        Every flow this tenant has saved, most recently published first. Editing a template
        publishes its next version; earlier versions are kept, because a published version is
        never changed and anything already pointing at one keeps running it. The current
        version&rsquo;s ID is what Test call and the API&rsquo;s <code>template-id</code> take.
      </p>

      {error ? (
        <p className="card__error" role="alert">
          {error}
        </p>
      ) : null}

      {templates === null ? (
        <p className="card__note">Loading…</p>
      ) : templates.length === 0 ? (
        <p className="card__note">No templates yet. Save one from the Flow designer.</p>
      ) : (
        <table className="results-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Version</th>
              <th>Current version ID</th>
              <th>Published</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {templates.map((template) => (
              <tr key={template.flow_id}>
                <td>{template.name}</td>
                <td>
                  v{template.latest_version_number}
                  {template.version_count > 1 ? (
                    <span className="card__note"> of {template.version_count}</span>
                  ) : null}
                </td>
                <td>
                  <code className="probe__detail">{template.latest_version_id}</code>{" "}
                  <button
                    type="button"
                    className="btn--outline btn--sm"
                    onClick={() => void copy(template.latest_version_id)}
                  >
                    {copiedId === template.latest_version_id ? "Copied" : "Copy"}
                  </button>
                </td>
                <td>{new Date(template.latest_published_at).toLocaleString()}</td>
                <td>
                  <button
                    type="button"
                    className="btn--outline btn--sm"
                    onClick={() => openInDesigner(template.flow_id, { versionId: template.latest_version_id, readOnly: true })}
                  >
                    View
                  </button>{" "}
                  <button
                    type="button"
                    className="btn--outline btn--sm"
                    onClick={() => openInDesigner(template.flow_id, { readOnly: false })}
                  >
                    Edit
                  </button>{" "}
                  {template.version_count > 1 ? (
                    <button
                      type="button"
                      className="btn--outline btn--sm"
                      aria-expanded={expanded === template.flow_id}
                      onClick={() => void toggleVersions(template.flow_id)}
                    >
                      {expanded === template.flow_id ? "Hide versions" : "Versions"}
                    </button>
                  ) : null}{" "}
                  {canPlaceTestCall ? (
                    <button
                      type="button"
                      className="btn--sm"
                      onClick={() =>
                        navigateTo(
                          `${pathForTab("test-call")}?template=${encodeURIComponent(template.latest_version_id)}`,
                        )
                      }
                    >
                      Place test call
                    </button>
                  ) : null}{" "}
                  {confirming === template.flow_id ? (
                    <>
                      <button
                        type="button"
                        className="btn--sm"
                        disabled={busy === template.flow_id}
                        onClick={() => void archive(template.flow_id)}
                      >
                        {busy === template.flow_id ? "Deleting…" : "Confirm delete"}
                      </button>{" "}
                      <button type="button" className="btn--outline btn--sm" onClick={() => setConfirming(null)}>
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="btn--outline btn--sm"
                      onClick={() => setConfirming(template.flow_id)}
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {templates.map((template) =>
              expanded === template.flow_id ? (
                <tr key={template.flow_id + "-versions"}>
                  <td colSpan={5}>
                    {versions[template.flow_id] === undefined ? (
                      <span className="card__note">Loading versions…</span>
                    ) : (
                      <ul className="card__note">
                        {(versions[template.flow_id] ?? []).map((version) => (
                          <li key={version.flow_version_id}>
                            v{version.version_number} — {new Date(version.published_at).toLocaleString()}{" "}
                            <code className="probe__detail">{version.flow_version_id}</code>{" "}
                            <button
                              type="button"
                              className="btn--outline btn--sm"
                              onClick={() =>
                                openInDesigner(template.flow_id, {
                                  versionId: version.flow_version_id,
                                  readOnly: true,
                                })
                              }
                            >
                              View
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                </tr>
              ) : null,
            )}
          </tbody>
        </table>
      )}
    </section>
  );
}
